import { portfolioData } from "../components/protfolio/portfolioData";
import NoImage from "../assets/images/no-image.png";

// Import all images from the portfolio directory
const portfolioImages = {};
// Using require.context to load all images from src/assets/images/portfolio
const r = require.context('../assets/images/portfolio', false, /\.(png|jpe?g|svg)$/);

r.keys().forEach((item) => { 
  // key is like "./filename.jpg" or "./folder/filename.jpg"
  const fileName = item.replace('./', '');
  portfolioImages[fileName] = r(item); 
});

// Using require.context to load all images from subfolders as well
const rGallery = require.context('../assets/images/portfolio', true, /\.(png|jpe?g|svg|webp)$/);
const galleryImages = {};
rGallery.keys().forEach((item) => {
  const path = item.replace('./', '');
  galleryImages[path] = rGallery(item);
});

export const getGalleryImagesForFolder = (folderName) => {
  if (!folderName) return [];
  return Object.keys(galleryImages)
    .filter(path => path.startsWith(folderName + '/'))
    .map(path => galleryImages[path]);
};

/**
 * Finds a matching image for a given project name.
 * Performs a case-insensitive check and ignores spaces.
 * 
 * @param {string} projectName - The name of the project.
 * @returns {string} The image source or the NoImage placeholder.
 */
export const getImageForProject = (projectName) => {
  if (!projectName) return NoImage;

  // Normalize project name for comparison (remove spaces, lowercase)
  const normalizedProjectName = projectName.toLowerCase().replace(/\s+/g, '');
  
  // Find a matching image file
  const matchingKey = Object.keys(portfolioImages).find(fileName => {
    const normalizedFileName = fileName.toLowerCase().replace(/\s+/g, '');
    return normalizedFileName.includes(normalizedProjectName);
  });

  return matchingKey ? portfolioImages[matchingKey] : NoImage;
};


/**
 * Returns a list of all portfolio projects.
 * Merges static data from portfolioData with the images found in the directory.
 * Ensures all images are represented, even if not in portfolioData.
 * 
 * @returns {Array} List of project objects.
 */
export const getAllPortfolioProjects = () => {
  const usedImages = new Set();
  
  // 1. Map defined projects from portfolioData to their images
  const definedProjects = portfolioData.map(project => {
    // Use imageName if provided, otherwise fallback to projectName
    const searchName = project.imageName || project.projectName;
    const imageSrc = getImageForProject(searchName);
    
    // Find the actual filename key that matched (to mark as used)
    // We need to repeat a bit of the logic from getImageForProject to find the exact key
    if (imageSrc !== NoImage) {
      const normalizedSearchName = searchName.toLowerCase().replace(/\s+/g, '');
      const matchingKey = Object.keys(portfolioImages).find(fileName => {
        const normalizedFileName = fileName.toLowerCase().replace(/\s+/g, '');
        return normalizedFileName.includes(normalizedSearchName);
      });
      if (matchingKey) usedImages.add(matchingKey);
    }

    return {
      ...project,
      localImageSrc: imageSrc,
      // Ensure defaults if missing in portfolioData (though they shouldn't be based on user's file)
      _id: project.projectName,
    };
  });

  // 2. Find any images that weren't matched to a project in portfolioData
  const remainingImages = Object.keys(portfolioImages)
    .filter(fileName => !usedImages.has(fileName))
    .map(fileName => {
      // Remove extension to get project name
      const projectName = fileName.replace(/\.[^/.]+$/, "");
      return {
        projectName: projectName,
        localImageSrc: portfolioImages[fileName],
        // Default static values for found images without metadata
        _id: projectName,
        Section: "WEB",
        technology: ["React", "Node.js"],
        shortDescription: "a motion designer and 3D artist turning ideas into engaging, brand-driven visuals.",
      };
    });

  return [...definedProjects, ...remainingImages];
};
